import { useState } from 'react'
import { useClientIntake } from '../../../assessment/hooks/useClientIntake'
import { useAuth } from '../../../../contexts/AuthContext'

/**
 * Admin-side card showing client intake form status with unlock/re-lock controls.
 * Used in the OverviewTab of the client detail page.
 */
function IntakeStatusCard({ client }) {
  const { user } = useAuth()
  const { status, intakeRecord, unlockIntake, relockIntake, error } = useClientIntake(client?.id, client)
  const [actionLoading, setActionLoading] = useState(false)

  const handleUnlock = async () => {
    setActionLoading(true)
    await unlockIntake(user?.id)
    setActionLoading(false)
  }

  const handleRelock = async () => {
    setActionLoading(true)
    await relockIntake()
    setActionLoading(false)
  }

  const statusConfig = {
    pending: {
      label: 'Pending',
      detail: 'Client has not yet submitted',
      bgColor: '#F8FAFC',
      borderColor: '#E2E8F0',
      dotColor: '#94A3B8'
    },
    submitted: {
      label: 'Submitted',
      detail: intakeRecord?.submitted_at
        ? `Submitted on ${new Date(intakeRecord.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
        : 'Client has submitted their profile',
      bgColor: '#F0FDF4',
      borderColor: '#BBF7D0',
      dotColor: '#22C55E'
    },
    unlocked: {
      label: 'Unlocked',
      detail: intakeRecord?.unlocked_at
        ? `Unlocked on ${new Date(intakeRecord.unlocked_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
        : 'Awaiting client revision',
      bgColor: '#FFFBEB',
      borderColor: '#FDE68A',
      dotColor: '#F59E0B'
    }
  }

  const config = statusConfig[status] || statusConfig.pending

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Client Intake</span>
        </div>
      </div>

      {/* Status indicator */}
      <div className="rounded-lg p-3" style={{ backgroundColor: config.bgColor, border: `1px solid ${config.borderColor}` }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config.dotColor }} />
            <span className="text-sm font-semibold" style={{ color: '#0F172A' }}>{config.label}</span>
          </div>
          {/* Lock/unlock toggle for submitted and unlocked states */}
          {status === 'submitted' && (
            <button
              onClick={handleUnlock}
              disabled={actionLoading}
              title="Unlock for client edits"
              className="flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 cursor-pointer"
              style={{ color: '#64748B' }}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              {actionLoading ? 'Unlocking...' : 'Locked'}
            </button>
          )}
          {status === 'unlocked' && (
            <button
              onClick={handleRelock}
              disabled={actionLoading}
              title="Re-lock intake form"
              className="flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-md border border-amber-300 dark:border-amber-600 transition-colors hover:bg-amber-50 dark:hover:bg-amber-900/30 disabled:opacity-50 cursor-pointer"
              style={{ color: '#F59E0B' }}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
              </svg>
              {actionLoading ? 'Locking...' : 'Re-Lock'}
            </button>
          )}
        </div>
        <p className="text-xs mt-1 ml-4" style={{ color: '#64748B' }}>{config.detail}</p>
      </div>

      {error && (
        <p className="text-xs text-red-600 mt-2">{error}</p>
      )}
    </div>
  )
}

export default IntakeStatusCard
